/**
 * Every server read and write in the app, in one place.
 *
 * Keeping the query keys and URL construction here (rather than inline in
 * components) means the set of things this frontend can ask for is auditable in
 * a single file -- which is how we keep spec section 71's "Jeabs API is never
 * called from the browser" checkable rather than merely intended.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  allianceActivityResponseSchema,
  allianceDetailResponseSchema,
  allianceMembersResponseSchema,
  bearAnalysisEnvelopeSchema,
  bearAssignmentsSavedSchema,
  bearDayDetailResponseSchema,
  bearDayListResponseSchema,
  bearDayResponseSchema,
  bearPlanResponseSchema,
  bearTrapSizingResponseSchema,
  bearSessionDetailResponseSchema,
  bearSessionListResponseSchema,
  bearCapabilitiesRecalculatedSchema,
  createdBearRallySchema,
  createdBearSessionSchema,
  dashboardResponseSchema,
  eventDetailResponseSchema,
  eventListResponseSchema,
  eventParticipantsUpdatedSchema,
  eventResponseSchema,
  persistedBearPlanSchema,
  rallyRecommendResponseSchema,
  meResponseSchema,
  personalBearAnalysisSchema,
  playerCapabilitiesResponseSchema,
  playerDetailResponseSchema,
  playerHeroesResponseSchema,
  playerHistoryResponseSchema,
  playerListResponseSchema,
  stateAlliancesResponseSchema,
  stateTransfersResponseSchema,
  strategyRulesResponseSchema,
  syncStatusSchema,
  type BearTrapNumber,
  type BearTrapPlanRequest,
  type CreateBearDay,
  type CreateBearRally,
  type CreateBearSession,
  type SetBearAssignments,
  type CreateEvent,
  type EventParticipantDto,
  type EventDto,
  type PersonalBearAnalysisDto,
  type RallyRecommendRequest,
} from '@kso/schemas';
import { useApi } from '../lib/auth';
import { ApiError, query } from '../lib/api';
import { tr } from '../i18n';

export type PlayerListParams = {
  search?: string;
  allianceId?: string;
  limit?: number;
  offset?: number;
};

export const queryKeys = {
  me: ['me'] as const,
  dashboard: ['dashboard'] as const,
  players: (params: PlayerListParams) => ['players', params] as const,
  player: (id: string) => ['player', id] as const,
  playerHistory: (id: string, days: number) => ['player', id, 'history', days] as const,
  playerHeroes: (id: string) => ['player', id, 'heroes'] as const,
  playerCapabilities: (id: string) => ['player', id, 'capabilities'] as const,
  bearAnalysis: (id: string) => ['bear', 'analysis', id] as const,
  bearSessions: (limit: number, offset: number) => ['bear', 'sessions', limit, offset] as const,
  bearSession: (id: string) => ['bear', 'session', id] as const,
  alliance: (id: string) => ['alliance', id] as const,
  allianceMembers: (id: string) => ['alliance', id, 'members'] as const,
  allianceActivity: (id: string) => ['alliance', id, 'activity'] as const,
  stateAlliances: (kingdomId: number) => ['state', kingdomId, 'alliances'] as const,
  stateTransfers: (kingdomId: number, days: number) =>
    ['state', kingdomId, 'transfers', days] as const,
  syncStatus: ['data', 'sync-status'] as const,
  strategyRules: ['data', 'rules'] as const,
  events: (filters: EventFilters) => ['events', filters] as const,
  event: (id: string) => ['event', id] as const,
  bearPlan: (runId: string) => ['bear', 'plan', runId] as const,
  bearDays: (allianceId: string) => ['bear', 'days', allianceId] as const,
  bearDay: (eventId: string) => ['bear', 'day', eventId] as const,
  bearTrapSizing: (eventId: string, trapNumber: number) =>
    ['bear', 'day', eventId, 'sizing', trapNumber] as const,
};

export type EventFilters = { eventType?: EventDto['eventType']; status?: EventDto['status'] };

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/**
 * The caller's own workspace row, including the player they are linked to.
 *
 * This is what makes `/bear/me` resolvable and what lets operator-only controls
 * be hidden rather than merely rejected. It is cached for the session because
 * roles do not change mid-shift.
 */
export function useCurrentUser() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => api.get('/api/me', meResponseSchema),
    staleTime: Infinity,
  });
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export function useDashboard() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: () => api.get('/api/dashboard', dashboardResponseSchema),
  });
}

// ---------------------------------------------------------------------------
// Players
// ---------------------------------------------------------------------------

export function usePlayers(params: PlayerListParams) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.players(params),
    queryFn: () =>
      api.get(
        `/api/players${query({
          search: params.search,
          allianceId: params.allianceId,
          limit: params.limit,
          offset: params.offset,
        })}`,
        playerListResponseSchema,
      ),
  });
}

export function usePlayer(playerId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.player(playerId ?? ''),
    queryFn: () =>
      api.get(`/api/players/${encodeURIComponent(playerId ?? '')}`, playerDetailResponseSchema),
    enabled: Boolean(playerId),
  });
}

export function usePlayerHistory(playerId: string | undefined, days: number) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.playerHistory(playerId ?? '', days),
    queryFn: () =>
      api.get(
        `/api/players/${encodeURIComponent(playerId ?? '')}/history${query({ days })}`,
        playerHistoryResponseSchema,
      ),
    enabled: Boolean(playerId),
  });
}

export function usePlayerHeroes(playerId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.playerHeroes(playerId ?? ''),
    queryFn: () =>
      api.get(
        `/api/players/${encodeURIComponent(playerId ?? '')}/heroes`,
        playerHeroesResponseSchema,
      ),
    enabled: Boolean(playerId),
  });
}

export function usePlayerCapabilities(playerId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.playerCapabilities(playerId ?? ''),
    queryFn: () =>
      api.get(
        `/api/players/${encodeURIComponent(playerId ?? '')}/capabilities`,
        playerCapabilitiesResponseSchema,
      ),
    enabled: Boolean(playerId),
  });
}

// ---------------------------------------------------------------------------
// Alliance
// ---------------------------------------------------------------------------

export function useAlliance(allianceId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.alliance(allianceId ?? ''),
    queryFn: () =>
      api.get(
        `/api/alliances/${encodeURIComponent(allianceId ?? '')}`,
        allianceDetailResponseSchema,
      ),
    enabled: Boolean(allianceId),
  });
}

export function useAllianceMembers(allianceId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.allianceMembers(allianceId ?? ''),
    queryFn: () =>
      api.get(
        `/api/alliances/${encodeURIComponent(allianceId ?? '')}/members`,
        allianceMembersResponseSchema,
      ),
    enabled: Boolean(allianceId),
  });
}

export function useAllianceActivity(allianceId: string | undefined, limit = 100) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.allianceActivity(allianceId ?? ''),
    queryFn: () =>
      api.get(
        `/api/alliances/${encodeURIComponent(allianceId ?? '')}/activity${query({ limit })}`,
        allianceActivityResponseSchema,
      ),
    enabled: Boolean(allianceId),
  });
}

// ---------------------------------------------------------------------------
// State intel
// ---------------------------------------------------------------------------

export function useStateAlliances(kingdomId: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.stateAlliances(kingdomId ?? 0),
    queryFn: () => api.get(`/api/state/${kingdomId}/alliances`, stateAlliancesResponseSchema),
    enabled: kingdomId !== undefined,
  });
}

export function useStateTransfers(kingdomId: number | undefined, days: number) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.stateTransfers(kingdomId ?? 0, days),
    queryFn: () =>
      api.get(`/api/state/${kingdomId}/transfers${query({ days })}`, stateTransfersResponseSchema),
    enabled: kingdomId !== undefined,
  });
}

// ---------------------------------------------------------------------------
// Bear
// ---------------------------------------------------------------------------

/**
 * The analysis arrives wrapped in an envelope that states emptiness explicitly.
 *
 * "Nothing has been recorded for this player" and "this player was analysed and
 * scored zero" are different claims, and the server refuses to let the UI
 * confuse them by inferring one from zeroed fields. `empty` is the authority;
 * the analysis body is only meaningful when it is false.
 */
export type BearAnalysisResult =
  | { empty: true; message: string; playerId: string }
  | { empty: false; analysis: PersonalBearAnalysisDto };

export function useBearAnalysis(playerId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.bearAnalysis(playerId ?? ''),
    queryFn: async (): Promise<BearAnalysisResult> => {
      const envelope = await api.get(
        `/api/bear/player/${encodeURIComponent(playerId ?? '')}/analysis`,
        bearAnalysisEnvelopeSchema,
      );

      if (envelope.empty) {
        return {
          empty: true,
          playerId: playerId ?? '',
          // The server's own wording wins when it sent one; this is the
          // fallback for an envelope that only set the flag.
          message: envelope.message ?? tr().bearAnalysis.noParticipations,
        };
      }

      // The envelope types `analysis` as unknown, so it is parsed here rather
      // than cast -- an analysis that does not match the contract must fail
      // loudly like every other response.
      const parsed = personalBearAnalysisSchema.safeParse(envelope.analysis);
      if (!parsed.success) {
        throw new ApiError(
          'CONTRACT_MISMATCH',
          tr().api.bearAnalysisContractMismatch(playerId ?? ''),
          200,
          parsed.error.issues
            .slice(0, 5)
            .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
            .join('; '),
        );
      }

      return { empty: false, analysis: parsed.data };
    },
    enabled: Boolean(playerId),
  });
}

export function useBearSessions(limit = 25, offset = 0) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.bearSessions(limit, offset),
    queryFn: () =>
      api.get(`/api/bear/sessions${query({ limit, offset })}`, bearSessionListResponseSchema),
  });
}

export function useBearSession(sessionId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.bearSession(sessionId ?? ''),
    queryFn: () =>
      api.get(
        `/api/bear/sessions/${encodeURIComponent(sessionId ?? '')}`,
        bearSessionDetailResponseSchema,
      ),
    enabled: Boolean(sessionId),
  });
}

export function useCreateBearSession() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBearSession) => {
      const created = await api.post('/api/bear/sessions', body, createdBearSessionSchema);
      return created.session;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bear', 'sessions'] });
    },
  });
}

export function useRecordBearRally(sessionId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBearRally) => {
      const created = await api.post(
        `/api/bear/sessions/${encodeURIComponent(sessionId)}/rallies`,
        body,
        createdBearRallySchema,
      );
      // `context` reports how far each member's bound gear snapshot sits from
      // the rally, and `warnings` what the server could not resolve. Both are
      // returned to the caller so the operator hears about a weak binding while
      // the result screen is still in front of them, not weeks later.
      return created.rally;
    },
    onSuccess: () => {
      // A newly recorded rally invalidates every personal analysis derived
      // from this session, and it moves the session's own rally and member
      // counts -- which are what tell an operator how much is still unentered.
      void queryClient.invalidateQueries({ queryKey: ['bear', 'analysis'] });
      void queryClient.invalidateQueries({ queryKey: ['bear', 'sessions'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bearSession(sessionId) });
    },
  });
}

/**
 * A stored plan run (spec sections 52, 73).
 *
 * Carries `generated`, an optional `manual` edit, and `effective`. The UI shows
 * `effective`; `generated` is kept so an R4 can see what they overrode, and is
 * never re-presented as current after an edit.
 */
export function useBearPlan(runId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.bearPlan(runId ?? ''),
    queryFn: () =>
      api.get(`/api/bear/plans/${encodeURIComponent(runId ?? '')}`, persistedBearPlanSchema),
    enabled: Boolean(runId),
  });
}

/**
 * Saves an operator's edit alongside the generated plan rather than over it.
 *
 * Spec section 73: the engine must not fight the human. The server keeps
 * `output` untouched and writes the edit to `manual_output`, so regenerating
 * later cannot silently discard a decision someone made deliberately.
 */
export function useSaveManualPlanEdit(runId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (edit: { manualOutput: unknown; note?: string }) =>
      api.patch(`/api/bear/plans/${encodeURIComponent(runId)}`, edit, persistedBearPlanSchema),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.bearPlan(runId), updated);
    },
  });
}

/*
 * Bear Trap DAYS (spec sections 19-20, 24, 30).
 *
 * A day carries both traps and the assignment of members between them. It is a
 * server-held object rather than screen state on purpose: the assignment is
 * what the alliance acts on, so "who is in BT1" has to survive a reload, and
 * the plan endpoint reads the SAVED roster rather than taking one from the
 * request.
 */

export function useBearDays(allianceId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.bearDays(allianceId ?? ''),
    queryFn: () =>
      api.get(`/api/bear/days${query({ allianceId })}`, bearDayListResponseSchema),
    enabled: Boolean(allianceId),
  });
}

export function useBearDay(eventId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.bearDay(eventId ?? ''),
    queryFn: () =>
      api.get(`/api/bear/days/${encodeURIComponent(eventId ?? '')}`, bearDayDetailResponseSchema),
    enabled: Boolean(eventId),
  });
}

/**
 * Opening a day is idempotent server-side: a second press returns the day that
 * already exists rather than failing, so this never has to guess whether one is
 * there before offering the button.
 */
export function useCreateBearDay() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBearDay) => {
      const created = await api.post('/api/bear/days', body, bearDayResponseSchema);
      return created.day;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bear', 'days'] });
    },
  });
}

/**
 * The save button behind the assignment screen.
 *
 * PUT, because the payload is the whole decision: a member left out of it loses
 * their assignment. Invalidates the sizing too -- how many leads a trap should
 * run depends on how many people are in it, so a stale figure next to a fresh
 * roster would be actively misleading.
 */
export function useSetBearAssignments(eventId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SetBearAssignments) =>
      api.put(
        `/api/bear/days/${encodeURIComponent(eventId)}/assignments`,
        body,
        bearAssignmentsSavedSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.bearDay(eventId) });
      void queryClient.invalidateQueries({ queryKey: ['bear', 'day', eventId, 'sizing'] });
      void queryClient.invalidateQueries({ queryKey: ['bear', 'days'] });
    },
  });
}

/**
 * How big to run one trap. A query, not a mutation: nothing is persisted, and
 * an operator asks it repeatedly while moving people between the two traps.
 */
export function useTrapSizing(eventId: string | undefined, trapNumber: BearTrapNumber) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.bearTrapSizing(eventId ?? '', trapNumber),
    queryFn: () =>
      api.get(
        `/api/bear/days/${encodeURIComponent(eventId ?? '')}/traps/${trapNumber}/sizing`,
        bearTrapSizingResponseSchema,
      ),
    enabled: Boolean(eventId),
  });
}

/**
 * Plans one trap against the roster saved for it.
 *
 * Deliberately sends no participant list. The saved assignment is the thing the
 * alliance acts on, and a plan built from an unsaved selection would describe a
 * roster that exists only in this tab.
 */
export function useGenerateTrapPlan(eventId: string, trapNumber: BearTrapNumber) {
  const api = useApi();
  return useMutation({
    mutationFn: (request: BearTrapPlanRequest) =>
      api.post(
        `/api/bear/days/${encodeURIComponent(eventId)}/traps/${trapNumber}/plan`,
        request,
        bearPlanResponseSchema,
      ),
  });
}

export function useRecalculateBearCapabilities() {
  const api = useApi();
  return useMutation({
    mutationFn: (playerIds: string[]) =>
      api.post('/api/bear/capabilities/recalculate', { playerIds }, bearCapabilitiesRecalculatedSchema),
  });
}

// ---------------------------------------------------------------------------
// Events (spec sections 19-20)
// ---------------------------------------------------------------------------

export function useEvents(filters: EventFilters = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.events(filters),
    queryFn: () =>
      api.get(
        `/api/events${query({ eventType: filters.eventType, status: filters.status })}`,
        eventListResponseSchema,
      ),
  });
}

export function useEvent(eventId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.event(eventId ?? ''),
    queryFn: () =>
      api.get(`/api/events/${encodeURIComponent(eventId ?? '')}`, eventDetailResponseSchema),
    enabled: Boolean(eventId),
  });
}

export function useCreateEvent() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEvent) => api.post('/api/events', body, eventResponseSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

/**
 * Cancelling an event does NOT delete it.
 *
 * The route soft-cancels by setting status to CANCELLED, because spec section
 * 1.4 makes history first class: an operation that was planned and called off
 * is itself a fact worth keeping. The UI labels this "Cancel" so the control
 * matches what actually happens.
 */
export function useCancelEvent() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      api.delete(`/api/events/${encodeURIComponent(eventId)}`, eventResponseSchema),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.event(updated.event.id), (previous: unknown) =>
        previous && typeof previous === 'object'
          ? { ...(previous as object), event: updated.event }
          : previous,
      );
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useSetEventParticipants(eventId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (participants: Array<Partial<EventParticipantDto> & { playerId: string }>) =>
      // PUT, not POST: this replaces the whole roster rather than appending to
      // it, which matches what the picker submits -- the complete list.
      api.put(
        `/api/events/${encodeURIComponent(eventId)}/participants`,
        { participants },
        eventParticipantsUpdatedSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.event(eventId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Rally Lab (spec section 36)
// ---------------------------------------------------------------------------

/**
 * A mutation, not a query: each run is persisted as a recommendation run with
 * its model version, so one should exist only when an operator asked for it.
 */
export function useRallyRecommend() {
  const api = useApi();
  return useMutation({
    mutationFn: (request: RallyRecommendRequest) =>
      api.post('/api/rally/recommend', request, rallyRecommendResponseSchema),
  });
}

// ---------------------------------------------------------------------------
// Data / operations
// ---------------------------------------------------------------------------

export function useSyncStatus() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.syncStatus,
    queryFn: () => api.get('/api/data/sync-status', syncStatusSchema),
    refetchInterval: 60_000,
  });
}

export function useStrategyRules() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.strategyRules,
    queryFn: () => api.get('/api/data/rules', strategyRulesResponseSchema),
  });
}
